<script setup lang="ts">
import LabelError from '../LabelError.vue';
import LabelBase from '../LabelBase.vue';
import InputBase from './InputBase.vue';

import { EFormItemSize } from '../../models/size';
import { ref } from 'vue';
import type { TInputOutlinedProps } from '../../models/TInputOutlinedProps';

const modelValue = defineModel<string>({ default: '' });

const props = withDefaults(defineProps<TInputOutlinedProps>(), {
  size: EFormItemSize.m40,
  absoluteErrors: true,
  isRequired: false,
  error: '',
  placeholder: '',
  label: '',
});

const emit = defineEmits<{
  (e: 'focus' | 'blur'): void;
}>();

const isFocused = ref(false);

const inputBaseEl = ref();

function onWrapperClicked() {
  inputBaseEl.value?.focus();
}

const focus = () => inputBaseEl.value?.focus();

defineExpose({ focus });

function onFocus() {
  isFocused.value = true;
  emit('focus');
}

function onBlur() {
  isFocused.value = false;
  emit('blur');
}
</script>

<template>
  <div
    class="input-outlined-wrapper"
    :class="{ 'input-outlined-wrapper_absolute-errors': props.absoluteErrors }"
    @click="onWrapperClicked"
  >
    <LabelBase :label="label" :is-required="isRequired" :is-disabled="disabled" />

    <div
      class="input-outlined-field"
      :class="{
        'input-outlined-field_focused': isFocused,
        'input-outlined-field_error': error,
        [`input-outlined-field_${size}`]: size,
        'input-outlined-field_disabled': disabled,
        'with-button': !!$slots.button,
      }"
    >
      <span v-if="$slots['prepend-icon']" class="input-outlined-field__prepend-icon">
        <slot name="prepend-icon" />
      </span>

      <InputBase
        ref="inputBaseEl"
        v-model="modelValue"
        :is-invalid="!!error"
        :size="size"
        :placeholder="placeholder"
        class="input-outlined-field__input"
        :disabled="disabled"
        @focus="onFocus"
        @blur="onBlur"
      />

      <span v-if="$slots['button']" class="input-outlined-field__button">
        <slot name="button" />
      </span>
    </div>

    <LabelError class="input-outlined-wrapper__error-label" :error-text="error" />
  </div>
</template>

<style lang="stylus" scoped>
.input-outlined-wrapper
  display flex
  flex-direction column
  gap 8px
  position relative

  &_absolute-errors
    & .input-outlined-wrapper__error-label
      text-align left
      position absolute
      bottom -18px
      left 0
      right 0

.input-outlined-field
  display flex
  align-items center
  padding 0 12px 0 6px
  cursor text
  background-color #FFFFFF
  border solid 1px var(--border-regular)
  border-radius var(--rounding150)
  box-sizing border-box

  &:hover
    border solid 1px var(--border-focused)
  &_focused
    border solid 1px var(--border-focused)
  &_error
    border solid 1px var(--border-negative)!important
  &_disabled
    border solid 1px var(--border-disabled)
    pointer-events none

  &__prepend-icon
    font-size 24px
    padding 6px
    display flex
    align-items center

  &.with-button
    padding-right 0

  &__button
    margin-left 12px
    height 100%

  &_m40
    height 40px
  &_l48
    height 48px

  &__input
    margin-left 10px
</style>
